import { Component, OnInit } from '@angular/core';
import { FileManagementService } from './file-management.service';
import { DirectoryInfo, FileInfo } from '../interfaces/interfaces';

@Component({
  selector: 'app-file-management',
  templateUrl: './file-management.component.html',
  styleUrls: ['./file-management.component.scss']
})
export class FileManagementComponent implements OnInit {
  directories: DirectoryInfo[] = [];
  files: FileInfo[] = [];
  error: string = '';
  loading: boolean = false;
  uploading: boolean = false;
  newDirectoryName: string = '';
  currentPath: string = '';
  pathSegments: string[] = [];

  constructor(private fileService: FileManagementService) { }

  ngOnInit(): void {
    this.loadContents();
  }

  loadContents(): void {
    this.loading = true;
    this.error = '';

    this.fileService.getFileManagement(this.currentPath).subscribe({
      next: (response) => {
        this.directories = response.directories;
        this.files = response.files;
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Error loading contents: ' + error.message;
        this.loading = false;
      }
    });
  }

  navigateToDirectory(dir: DirectoryInfo): void {
    this.currentPath = dir.path.replace('/Upload_Directory/General/', '');
    this.updatePathSegments();
    this.loadContents();
  }

  navigateToPath(index: number): void {
    if (index === -1) {
      // Navigate to root
      this.currentPath = '';
    } else {
      // Navigate to specific path segment
      this.currentPath = this.pathSegments.slice(0, index + 1).join('/');
    }
    this.updatePathSegments();
    this.loadContents();
  }

  private updatePathSegments(): void {
    this.pathSegments = this.currentPath ? this.currentPath.split('/') : [];
  }

  createDirectory(): void {
    if (!this.newDirectoryName) {
      this.error = 'Please enter a directory name';
      return;
    }

    const directoryPath = this.currentPath 
      ? `${this.currentPath}/${this.newDirectoryName}`
      : this.newDirectoryName;

    this.fileService.createDirectory(directoryPath).subscribe({
      next: () => {
        this.loadContents();
        this.newDirectoryName = '';
        this.error = '';
      },
      error: (error) => {
        this.error = 'Error creating directory: ' + error.message;
      }
    });
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (files.length > 0) {
      this.uploading = true;
      this.error = '';

      Array.from(files).forEach((file, index) => {
        this.fileService.uploadToDirectory(file, this.currentPath).subscribe({
          next: () => {
            if (index === files.length - 1) {
              this.loadContents();
              this.uploading = false;
            }
          },
          error: (error) => {
            this.error = `Error uploading ${file.name}: ${error.message}`;
            this.uploading = false;
          }
        });
      });
    }
  }

  downloadFile(file: FileInfo): void {
    this.fileService.downloadFile(file.path).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.error = 'Error downloading file: ' + error.message;
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleString();
  }

  deleteFile(file: FileInfo): void {
    if (confirm(`Are you sure you want to delete ${file.name}?`)) {
      this.loading = true;
      this.error = '';

      this.fileService.deleteFile(file.path).subscribe({
        next: () => {
          this.loadContents();
          this.loading = false;
        },
        error: (error) => {
          this.error = `Error deleting ${file.name}: ${error.message}`;
          this.loading = false;
        }
      });
    }
  }
} 